import React from 'react';
import { MdCheckCircle, MdErrorOutline, MdRadioButtonUnchecked } from 'react-icons/md';

export type ExtractionStage = 'idle' | 'uploading' | 'extracting-text' | 'analyzing' | 'done' | 'error';

type VisibleStage = 'uploading' | 'extracting-text' | 'analyzing';

const STAGE_ORDER: VisibleStage[] = ['uploading', 'extracting-text', 'analyzing'];

const STAGE_LABELS: Record<VisibleStage, string> = {
    'uploading': 'Enviando documento',
    'extracting-text': 'Extraindo texto do PDF',
    'analyzing': 'Analisando dados com IA',
};

export interface ExtractionProgressState {
    stage: ExtractionStage;
    fileName?: string;
    errorMessage?: string;
}

export const initialExtractionProgress: ExtractionProgressState = {
    stage: 'idle',
};

const StageRow: React.FC<{
    stage: VisibleStage;
    current: ExtractionStage;
}> = ({ stage, current }) => {
    const currentIndex = STAGE_ORDER.indexOf(current as VisibleStage);
    const stageIndex = STAGE_ORDER.indexOf(stage);
    const isDone = current === 'done' || (currentIndex > -1 && stageIndex < currentIndex);
    const isActive = stage === current;

    let icon = <MdRadioButtonUnchecked className="text-gray-300 dark:text-slate-600" size={18} />;
    if (isDone) {
        icon = <MdCheckCircle className="text-green-500" size={18} />;
    } else if (isActive) {
        icon = (
            <span
                className="block h-[18px] w-[18px] animate-spin rounded-full border-2 border-blue-500 border-t-transparent"
                aria-hidden="true"
            />
        );
    }

    return (
        <li className="flex items-center gap-3">
            <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center">{icon}</span>
            <span
                className={`text-sm ${
                    isActive
                        ? 'font-semibold text-gray-900 dark:text-gray-100'
                        : isDone
                          ? 'text-gray-600 dark:text-gray-400'
                          : 'text-gray-400 dark:text-slate-500'
                }`}
            >
                {STAGE_LABELS[stage]}
            </span>
        </li>
    );
};

/**
 * Shows extraction progress with stages: upload → text extraction → AI analysis.
 * Replaces the generic spinner during document processing.
 */
const ExtractionProgressPanel: React.FC<{ progress: ExtractionProgressState }> = ({ progress }) => {
    if (progress.stage === 'idle' || progress.stage === 'done') return null;

    const { stage, fileName, errorMessage } = progress;

    if (stage === 'error') {
        return (
            <div
                className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950"
                role="alert"
            >
                <MdErrorOutline className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" size={20} />
                <div>
                    <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                        Não foi possível extrair os dados do documento
                    </p>
                    {errorMessage && (
                        <p className="mt-1 text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <section
            className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-slate-700 dark:bg-slate-800"
            aria-live="polite"
            aria-busy="true"
        >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Processando documento
                    {fileName && (
                        <span className="ml-2 rounded bg-white px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-slate-700 dark:text-blue-300">
                            {fileName}
                        </span>
                    )}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                    Aguarde enquanto extraímos os dados do contrato
                </p>
            </div>

            <ul className="space-y-2">
                <StageRow stage="uploading" current={stage} />
                <StageRow stage="extracting-text" current={stage} />
                <StageRow stage="analyzing" current={stage} />
            </ul>
        </section>
    );
};

export default ExtractionProgressPanel;
